import { ERLAUBTE_SCHEMATA_RECHTSTEXT, baueTeile, teileAlsText, zerlegeTeile, } from './textformat.js';
/**
 * Rechtstexte in Markdown — Impressum und Datenschutz (Änderungsanforderung ÄA-5).
 *
 * **Gespeichert, bearbeitet und ausgetauscht wird Markdown**, nicht HTML. Eine vorhandene
 * `.md`-Datei lässt sich damit unverändert einsetzen, und der Administrator sieht im Editor genau
 * den Text, der in der Datenbank steht.
 *
 * Die Auszeichnung ist dieselbe wie bei Abstimmungstexten nach §12 — `**fett**`, `*kursiv*`,
 * `- Aufzählung`, `1. Nummerierung`, `[Text](https://…)` — erweitert um **Überschriften**
 * `# ` bis `###### ` und um `mailto:` als Linkziel. Die Inline-Zerlegung stammt unverändert aus
 * `textformat.ts`; neu sind hier ausschließlich die Blockebene und die weitere Schemaliste.
 *
 * **Die Sicherheitszusage steckt in der Bauweise, nicht in einem Filter.** {@link zerlegeMarkdown}
 * liefert einen typisierten Baum; die Oberfläche macht daraus React-Elemente. In keinem
 * Anzeigepfad entsteht ein HTML-String. Was der Zerleger nicht kennt, bleibt gewöhnlicher Text —
 * `<script>alert(1)</script>` wird angezeigt, nicht ausgeführt, genau wie jedes andere Zeichen.
 *
 * Gespeichert wird `normalisiereMarkdown(eingabe)`. Was in der Datenbank liegt, hat den Zerleger
 * also bereits passiert und wird später genau so wieder gelesen.
 *
 * **Dieses Modul erzeugt an keiner Stelle HTML.** Bis zum Wegfall des WYSIWYG-Editors gab es hier
 * zwei Brückenfunktionen, die für eine bearbeitbare Fläche HTML aufbauten und wieder einlasen;
 * mit dem Editor sind sie entfallen (Entscheidung E38 in `docs/ARCHITECTURE.md`).
 */
/** Linkschemata der Rechtstexte: wie §12, zusätzlich `mailto:`. */
const SCHEMATA = ERLAUBTE_SCHEMATA_RECHTSTEXT;
/** Überschrift: ein bis sechs Doppelkreuze, dann Leerraum. */
const UEBERSCHRIFT = /^(#{1,6})\s+(.*)$/;
/** Aufzählungspunkt: `- ` oder `* ` am Zeilenanfang — wie in §12. */
const AUFZAEHLUNG = /^[-*]\s+(.*)$/;
/** Nummerierter Punkt: `1. ` am Zeilenanfang — wie in §12. */
const NUMMERIERT = /^\d+[.)]\s+(.*)$/;
/**
 * Zerlegt einen Rechtstext in Blöcke.
 *
 * Absätze werden durch Leerzeilen getrennt, aufeinanderfolgende Zeilen eines Absatzes
 * zusammengefasst. Eine Überschrift steht allein auf ihrer Zeile und beendet Absatz wie Liste.
 * Aufeinanderfolgende Listenzeilen bilden eine Liste; ein Wechsel zwischen Aufzählung und
 * Nummerierung beginnt eine neue.
 *
 * Der Zerleger bricht bei keiner Eingabe ab: Was er nicht als Auszeichnung erkennt, wird Text.
 */
export function zerlegeMarkdown(markdown) {
    const bloecke = [];
    const zeilen = markdown.replace(/\r\n?/g, '\n').split('\n');
    let absatz = [];
    let liste;
    function absatzAbschliessen() {
        if (absatz.length > 0) {
            // Je Zeile eine eigene Zerlegung — die Umbrüche bleiben erhalten (siehe {@link RichBlock}).
            bloecke.push({ art: 'absatz', zeilen: absatz.map((zeile) => zerlegeTeile(zeile, SCHEMATA)) });
            absatz = [];
        }
    }
    function listeAbschliessen() {
        if (liste !== undefined) {
            bloecke.push({
                art: 'liste',
                nummeriert: liste.nummeriert,
                punkte: liste.punkte.map((punkt) => zerlegeTeile(punkt, SCHEMATA)),
            });
            liste = undefined;
        }
    }
    for (const zeile of zeilen) {
        const inhalt = zeile.trim();
        if (inhalt === '') {
            absatzAbschliessen();
            listeAbschliessen();
            continue;
        }
        const ueberschrift = UEBERSCHRIFT.exec(inhalt);
        if (ueberschrift !== null) {
            absatzAbschliessen();
            listeAbschliessen();
            const text = (ueberschrift[2] ?? '').trim();
            if (text !== '') {
                bloecke.push({
                    art: 'ueberschrift',
                    stufe: (ueberschrift[1] ?? '#').length,
                    teile: zerlegeTeile(text, SCHEMATA),
                });
            }
            continue;
        }
        const aufzaehlung = AUFZAEHLUNG.exec(inhalt);
        const nummeriert = NUMMERIERT.exec(inhalt);
        const punkt = aufzaehlung ?? nummeriert;
        if (punkt !== null) {
            absatzAbschliessen();
            const istNummeriert = nummeriert !== null;
            if (liste !== undefined && liste.nummeriert !== istNummeriert) {
                listeAbschliessen();
            }
            liste ??= { nummeriert: istNummeriert, punkte: [] };
            liste.punkte.push(punkt[1] ?? '');
            continue;
        }
        listeAbschliessen();
        absatz.push(inhalt);
    }
    absatzAbschliessen();
    listeAbschliessen();
    return bloecke;
}
/**
 * Setzt den Baum wieder zu Markdown zusammen — die kanonische Fassung.
 *
 * Modulintern: Nach außen führt {@link normalisiereMarkdown} als die eine Tür, durch die jede
 * Eingabe läuft. Das Ergebnis ist idempotent — ein erneuter Durchlauf ändert nichts mehr, und nur
 * so kann ein einmal gespeicherter Text später nicht plötzlich anders gelesen werden.
 */
function serialisiereMarkdown(bloecke) {
    return bloecke.map(baueBlock).join('\n\n');
}
function baueBlock(block) {
    switch (block.art) {
        case 'absatz':
            return block.zeilen.map(baueTeile).join('\n');
        case 'ueberschrift':
            return `${'#'.repeat(block.stufe)} ${baueTeile(block.teile)}`;
        case 'liste':
            return block.punkte
                .map((punkt, index) => `${block.nummeriert ? `${index + 1}.` : '-'} ${baueTeile(punkt)}`)
                .join('\n');
    }
}
/**
 * Bereinigt einen Rechtstext serverseitig (§25.2).
 *
 * Die eine Stelle, durch die jede Eingabe läuft, bevor sie gespeichert wird.
 */
export function normalisiereMarkdown(markdown) {
    return serialisiereMarkdown(zerlegeMarkdown(markdown));
}
/** Der reine Text ohne jede Auszeichnung — für Kurzfassungen und Prüfungen. */
export function nurTextAusMarkdown(markdown) {
    return zerlegeMarkdown(markdown)
        .map((block) => {
        switch (block.art) {
            case 'absatz':
                return block.zeilen.map((zeile) => teileAlsText(zeile)).join('\n');
            case 'liste':
                return block.punkte.map((punkt) => teileAlsText(punkt)).join('\n');
            case 'ueberschrift':
                return teileAlsText(block.teile);
        }
    })
        .join('\n\n');
}
//# sourceMappingURL=richtext.js.map